import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { ModalController } from '@ionic/angular/standalone';

export interface ConfirmationDialogData {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'red' | 'sky';
}

export const CONFIRMATION_DIALOG_ID = 'confirmation-dialog';

@Component({
  selector: 'dojo-confirmation-dialog',
  standalone: true,
  imports: [ButtonComponent, IconComponent],
  template: `
    <div class="flex flex-col p-6">
      <div class="mb-4 flex items-center justify-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
          <dojo-icon [icon]="'triangle-exclamation'" [size]="24" class="text-red-600 dark:text-red-400" />
        </div>
      </div>
      <h3 class="mb-2 text-center text-xl font-semibold text-gray-900 dark:text-gray-100">{{ title() }}</h3>
      <p class="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">{{ message() }}</p>
      <div class="flex flex-col gap-2">
        <dojo-button (click)="closeDialog()">{{ cancelLabel() }}</dojo-button>
        <dojo-button (click)="confirm()" [fill]="'outline'" [customColor]="confirmColor()">
          {{ confirmLabel() }}
        </dojo-button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent {
  private readonly modalController = inject(ModalController);

  readonly title = input<string>('');
  readonly message = input<string>('');
  readonly confirmLabel = input<string>('Confirm');
  readonly cancelLabel = input<string>('Cancel');
  readonly confirmColor = input<'red' | 'sky'>('red');

  confirm(): void {
    this.modalController.dismiss(true, undefined, CONFIRMATION_DIALOG_ID);
  }

  closeDialog(): void {
    this.modalController.dismiss(false, undefined, CONFIRMATION_DIALOG_ID);
  }
}
